/**
 * F-0413 (AC-18.5): the bar's title for the view under it (`barTitleOf`). Its own module, apart from
 * the bar's selection (`bar-naming.ts`, `barNamingOf`), because the title is read on every first
 * screen and the selection needs the shell contract and its route table, which a launch does not
 * load (I-2453, `scripts/check-first-screen-chunks.mjs`). `bar-naming.ts` re-exports all of it.
 *
 * ⛔ Platform-agnostic like the rest of `@featit/shared`: the pathname is all it reads.
 */

import { appPath, parseAddress } from './addressing/paths.js';
import {
  recordLabel,
  viewLabel,
  type ChromeLabelType,
  type ChromeTitleKeyType,
} from './chrome-label.js';
import { shellPathname } from './shell-paths.js';

/** What the bar's title says about the view. */
export interface IBarTitle {
  /**
   * What the title names: a view by its `chromeTitles` key, translated where it is drawn, or a
   * record by its own name (T-0838, `chrome-label.ts`).
   */
  readonly label: ChromeLabelType;
  /** The title says which view this is, so a page under it need not repeat it (`I-1065`). */
  readonly namesTheView: boolean;
  /**
   * Which view the title was decided for, the same key at every scope and namespace depth
   * (`items` for `/-/items` and `/acme/tracker/-/items`), or `null` when no entry answered and the
   * title is the fallback. Two keys with one title are a decision written in
   * {@link BAR_SHARED_TITLES}; the route walk (`web/src/components/barTitles.guard.test.ts`) holds
   * every route to it.
   */
  readonly view: string | null;
}

export interface IBarTitleOptions {
  /** A custom page's or an opened view's own name, once it is known (I-1366 for a view). */
  readonly pageTitle?: string | null;
  /**
   * The address frame's title, for a state that is not a page (`workAreaRouteState.ts`): its view
   * label, translated where it is drawn (I-2362), or a text drawn as it is.
   */
  readonly frameTitle?: ChromeLabelType | string | null;
}

/** What the bar says when no entry answers. Only a home may legitimately be called this. */
export const BAR_FALLBACK_LABEL: ChromeLabelType = viewLabel('home');

/**
 * ⭐ **The titles more than one view shares, and why.** Anything else the route walk finds sharing a
 * title is a view without a name of its own, which is how `/-/admin` read "Home" for months.
 */
export const BAR_SHARED_TITLES: Readonly<Partial<Record<ChromeTitleKeyType, string>>> = {
  'chromeTitles.home':
    'A home, in any scope, and a custom page until its name has loaded; only the home is named by it.',
  'chromeTitles.settings':
    'Global, work area and organisation settings share the word, so it names none of them and each page keeps its own heading.',
  'chromeTitles.decisionsWaiting':
    'One decision opens beside the list, so the bar keeps the list’s name; it does not say which decision.',
  'chromeTitles.tasksWaiting':
    'One task opens beside the list, so the bar keeps the list’s name; it does not say which task.',
  'chromeTitles.views':
    'A view opened from Views is called by its own name once it is known (I-1366); until the saved views have answered it keeps the catalog’s name.',
};

const named = (label: ChromeLabelType, view: string): IBarTitle => ({
  label,
  namesTheView: true,
  view,
});
const generic = (label: ChromeLabelType, view: string): IBarTitle => ({
  label,
  namesTheView: false,
  view,
});

/**
 * The segments that name the view, and the scope they sit in: the route after a work area's `/-/`,
 * after `/-/work-areas`, after `/-/namespaces/:id`, or after `/-/`. A view is named by the segment in
 * the view's position and nothing else, so a slug or a namespace can never be read as one (I-0385).
 */
function barViewSegments(pathname: string): {
  scope: 'app' | 'work-areas' | 'namespace' | 'work-area' | null;
  segments: string[];
} {
  const address = parseAddress(pathname);
  if (address.kind === 'work-area') {
    return { scope: 'work-area', segments: address.route.split('/').filter(Boolean) };
  }
  if (address.kind !== 'app') return { scope: null, segments: [] };
  const segments = address.route.split('/').filter(Boolean);
  if (segments[0] === 'work-areas') return { scope: 'work-areas', segments: segments.slice(1) };
  // F-0383: a namespace's scope is All work areas narrowed to its subtree, one segment deeper.
  if (segments[0] === 'namespaces' && segments.length >= 2) {
    return { scope: 'namespace', segments: segments.slice(2) };
  }
  return { scope: 'app', segments };
}

/**
 * ⭐ **The bar's title for a pathname**, and whether it names the view. Matched on the view's own
 * segments, never on substrings of the path: the slug is in the path too, and `documents-archive`
 * used to name every page of its work area (I-0385).
 */
export function barTitleOf(pathname: string, options: IBarTitleOptions = {}): IBarTitle {
  const { pageTitle, frameTitle } = options;
  // A state that is not a page says what it is in its own words (`workAreaRouteState.ts`).
  if (frameTitle) {
    return named(typeof frameTitle === 'string' ? recordLabel(frameTitle) : frameTitle, 'frame');
  }
  const path = shellPathname(pathname);
  if (path.startsWith(`${appPath('/work-areas/choose')}/`))
    return named(viewLabel('chooseWorkArea'), 'choose');
  // F-0530 (AC-19.4): the native switcher's Edit view, not the Work areas overview it sits beside.
  if (path === appPath('/work-areas/edit'))
    return named(viewLabel('editWorkAreas'), 'edit-work-areas');
  const address = parseAddress(path);
  if (address.kind === 'namespace') return generic(viewLabel('workAreas'), 'namespace');
  const { scope, segments } = barViewSegments(path);
  const [view, detail, section] = segments;
  if (scope === 'work-area' && segments.length === 0) return named(viewLabel('home'), 'home');
  if (scope === 'app' && view === 'home' && !detail) return named(viewLabel('home'), 'home');
  // I-1065: All work areas' home and a namespace's are a home too, as the sidebar's Home line and
  // the tab's title ("Work areas - Home") already say; the scope line above the bar says which.
  if ((scope === 'work-areas' || scope === 'namespace') && segments.length === 0) {
    return named(viewLabel('home'), 'home');
  }
  if (view === 'items') {
    if (detail === 'new') return named(viewLabel('newWorkItem'), 'new-item');
    return detail ? generic(viewLabel('workItem'), 'item') : named(viewLabel('workItems'), 'items');
  }
  if (view === 'activity') return named(viewLabel('activity'), 'activity');
  if (view === 'decisions') {
    if (detail === 'new') return named(viewLabel('newDecision'), 'new-decision');
    return detail
      ? generic(viewLabel('decisionsWaiting'), 'decision')
      : named(viewLabel('decisionsWaiting'), 'decisions');
  }
  if (view === 'tasks') {
    return detail
      ? generic(viewLabel('tasksWaiting'), 'task')
      : named(viewLabel('tasksWaiting'), 'tasks');
  }
  if (view === 'documents')
    return detail
      ? generic(viewLabel('document'), 'document')
      : named(viewLabel('documents'), 'documents');
  if (view === 'roadmap') return named(viewLabel('roadmap'), 'roadmap');
  // F-0321: a work area's Parts tree, and one part's page.
  if (view === 'parts') {
    return detail ? generic(viewLabel('part'), 'part') : named(viewLabel('parts'), 'parts');
  }
  // F-0424: a work area's Schedules, and one schedule's page (a run opens inside it).
  if (view === 'schedules') {
    // I-1896: a run's own page (`schedules/<slug>/runs/<id>`, a phone's) is the run, not its schedule.
    if (detail && section === 'runs' && segments[3])
      return generic(viewLabel('run'), 'schedule-run');
    // I-1955: a schedule's form names what it does, not the schedule it edits.
    if (detail && detail !== 'new' && section === 'edit')
      return generic(viewLabel('editSchedule'), 'edit-schedule');
    return detail && detail !== 'new'
      ? generic(viewLabel('schedule'), 'schedule')
      : detail === 'new'
        ? named(viewLabel('newSchedule'), 'new-schedule')
        : named(viewLabel('schedules'), 'schedules');
  }
  // I-1366 (IL-18a): a view opened from Views is called by its own name, so its title never repeats
  // Back's "Views"; the catalog's name only until that name is known.
  if (view === 'views') {
    if (!detail) return named(viewLabel('views'), 'views');
    return pageTitle ? named(recordLabel(pageTitle), 'view') : generic(viewLabel('views'), 'view');
  }
  if (view === 'settings') {
    return scope === 'work-area'
      ? generic(viewLabel('settings'), 'work-area-settings')
      : generic(viewLabel('settings'), 'global-settings');
  }
  // AC-18.5: the Global settings page (`AdminPage`) at its older address, titled as that page is.
  if (scope === 'app' && view === 'admin') return generic(viewLabel('settings'), 'global-settings');
  if (view === 'profile') return named(viewLabel('profile'), 'profile');
  if (view === 'more') return named(viewLabel('more'), 'more');
  // A custom page is called what it is called, once its definition has loaded.
  if (scope === 'work-area' && view === 'pages' && detail) {
    return pageTitle ? named(recordLabel(pageTitle), 'page') : generic(BAR_FALLBACK_LABEL, 'page');
  }
  // F-0601 AMX-7 (T-0766): Infrastructure and a machine's Permissions are no views of their own; both addresses only
  // redirect to the Agents explorer (AMX-6), so while the redirect reads, the bar already says where it lands.
  if (view === 'infrastructure' || view === 'agents') return named(viewLabel('agents'), 'agents');
  // F-0630 (D-0359 c): every agent conversation, and one opened in it, under one name.
  if (view === 'conversations') return named(viewLabel('conversations'), 'conversations');
  // F-0564: a commit's changed files and diff, opened from an item's Commits or from Activity.
  if (scope === 'work-area' && view === 'commits' && detail)
    return generic(viewLabel('commit'), 'commit');
  // U-0023: a repository file named in an item's text, read on the reader's machine. U-0060: an
  // address that names no file (`files`) is still that page ("This address names no file"), as the tab
  // calls it; it fell to the bar's fallback before.
  if (scope === 'work-area' && view === 'files') return generic(viewLabel('file'), 'file');
  // I-1774: a file in a named folder, `folders/<folder>@<device>/files/<path>`.
  if (scope === 'work-area' && view === 'folders' && detail)
    return generic(viewLabel('file'), 'file');
  // F-0347 (I-1023): an organisation's own Settings, ahead of the organisation's page.
  if (view === 'organisations' && section === 'settings') {
    return generic(viewLabel('settings'), 'organisation-settings');
  }
  // I-0334: an organisation's People page.
  if (view === 'organisations') return generic(viewLabel('organisation'), 'organisation');
  if (view === 'invite') return named(viewLabel('invitation'), 'invite');
  // F-0271: what is shared with a guest. F-0710 CRQ-12 (I-2492): one request opened on its own
  // (`shared/<workAreaId>/<key>`) is called by its title once its page has read it, so a phone,
  // whose page heading the bar replaces, still says which request it is.
  if (view === 'shared') {
    return detail && section && pageTitle
      ? named(recordLabel(pageTitle), 'shared')
      : named(viewLabel('sharedWithYou'), 'shared');
  }
  // F-0461 (OF-31): the ideas the reader reported.
  if (view === 'my-ideas') return named(viewLabel('myIdeas'), 'my-ideas');
  // AC-AD12: a feedback entry's detail, which named itself Home.
  if (view === 'feedback') return generic(viewLabel('feedback'), 'feedback');
  if (scope === 'work-areas') return generic(viewLabel('workAreas'), 'work-areas');
  return { label: BAR_FALLBACK_LABEL, namesTheView: false, view: null };
}
